/**
 * One reading of a live call, as numbers a chart can be drawn from.
 *
 * callDiagnostics.ts answers «what went wrong», once, at the end, in a sentence
 * meant to be read by eye. This answers «what was it doing at 0:35», every few
 * seconds, in columns meant to be averaged and plotted — which is a different
 * shape even where it is the same underlying field. The two share their reading
 * of `getStats`: everything here is built on the same pure helpers, so a route
 * or a quality limitation means exactly what it means in a `call/quality` row.
 *
 * Rates rather than counters, because a counter is what the browser keeps and a
 * rate is what anyone asks about: «сколько шло видео» is bits per second over
 * the last few seconds, and the difference between two cumulative byte counts is
 * the only place that exists. Hence the cursor — a sample is a function of this
 * reading *and* the previous one, and the first sample of a call has no rates at
 * all rather than fabricated ones.
 *
 * Pure, like its neighbour: no timers, no fetch, no peer connection. The clock
 * and the transport belong to callTelemetry.ts.
 */

import { limitation, selectedPair, streamKind, summariseConnection, type StatEntry } from './callDiagnostics.ts';

/** What one side reported at one moment. Mirrors the call_samples columns. */
export interface CallSample {
  /** The device's clock. The server keeps its own arrival time separately. */
  at: number;
  /** Milliseconds since the previous reading; null on the first of a call. */
  intervalMs: number | null;
  audioOutBytes: number | null;
  audioInBytes: number | null;
  videoOutBytes: number | null;
  videoInBytes: number | null;
  audioOutBps: number | null;
  audioInBps: number | null;
  videoOutBps: number | null;
  videoInBps: number | null;
  /**
   * How loud each direction actually was over this interval, 0..1 RMS.
   *
   * Beside the bitrates rather than instead of them, because they answer
   * different questions and a quiet call answers only this one: Opus spends
   * much the same bitrate on a whisper as on a voice, so a call nobody can hear
   * is 30 kbit/s of perfectly delivered near-silence. Out is this device's own
   * microphone, in is what arrived — and which of the two is small is the whole
   * diagnosis.
   */
  audioOutLevel: number | null;
  audioInLevel: number | null;
  fpsOut: number | null;
  fpsIn: number | null;
  widthOut: number | null;
  heightOut: number | null;
  widthIn: number | null;
  heightIn: number | null;
  rttMs: number | null;
  jitterMs: number | null;
  /** Lost share of the packets *of this interval*, 0–1. */
  loss: number | null;
  availableOutBps: number | null;
  limitation: string | null;
  limitedShare: number | null;
  freezes: number | null;
  freezeMs: number | null;
  route: string | null;
  relayed: boolean | null;
  codecOut: string | null;
  codecIn: string | null;
  /** Small and usually absent — see StatEntry's encoderImplementation. */
  extra: Record<string, string | number> | null;
}

/**
 * What the next sample needs to know about this one. Only the cumulative
 * figures: everything else is read afresh, and keeping more would be keeping a
 * second copy of the sample for no one.
 */
export interface StatsCursor {
  at: number;
  audioOutBytes: number | null;
  audioInBytes: number | null;
  videoOutBytes: number | null;
  videoInBytes: number | null;
  packetsReceived: number | null;
  packetsLost: number | null;
  /** Cumulative, and only ever read as the difference between two readings. */
  audioOutEnergy: number | null;
  audioOutSeconds: number | null;
  audioInEnergy: number | null;
  audioInSeconds: number | null;
}

/**
 * The shortest gap between two readings that a rate can honestly be computed
 * over.
 *
 * The closing reading is taken the moment a call ends, which can be a fraction
 * of a second after the tick before it — and over that fraction the byte
 * counters have often not moved at all, so the arithmetic is right and the
 * answer is «0 bit/s» on a call that was sending two megabits. Every chart
 * would end on a cliff to zero. A quarter of a second is far below the usual
 * interval and far above the gap that produces this.
 */
const MIN_INTERVAL_MS = 250;

/**
 * And the shortest stretch of *audio* an average may be taken over. Separate
 * from the interval above because it is a different clock: `totalSamplesDuration`
 * counts audio that actually played, which stops while a track is muted and
 * lags a wall clock that never does.
 */
const MIN_VOICE_SECONDS = 0.25;

function number(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Bits per second between two cumulative readings.
 *
 * Null rather than zero for anything that cannot be a rate: no previous reading,
 * no interval, or a counter that went *down*. That last one is not noise — a
 * sender handed a fresh track starts its bytes again from zero (the microphone
 * restart does exactly this), and calling that «-4 Mbit/s» would put a spike
 * through the middle of every chart the moment a call recovered.
 */
function rate(current: number | null, previous: number | null, intervalMs: number | null): number | null {
  if (current === null || previous === null || intervalMs === null) return null;
  if (intervalMs < MIN_INTERVAL_MS) return null;
  const delta = current - previous;
  if (delta < 0) return null;
  return Math.round((delta * 8 * 1000) / intervalMs);
}

/**
 * The RMS amplitude of one direction over one interval, 0..1, or null.
 *
 * The standard hands out `totalAudioEnergy` — the sum of every sample's square
 * — and `totalSamplesDuration` beside it, both cumulative over the call. The
 * energy of an interval divided by its duration is the mean square over that
 * interval, and the root of that is the RMS a person would call loudness. The
 * instantaneous `audioLevel` is not used for the reason a five-second sampling
 * period makes obvious: it is one 100 ms window, and a window that lands
 * between two words reads zero on a call nobody had any trouble hearing.
 *
 * Null for anything that cannot honestly be an average: no previous reading, a
 * counter that went backwards (a fresh track restarts both — see
 * restartMicrophone), or a stretch of samples too short to mean anything.
 */
function voiceLevel(
  energy: number | null,
  seconds: number | null,
  wasEnergy: number | null,
  wasSeconds: number | null,
): number | null {
  if (energy === null || seconds === null || wasEnergy === null || wasSeconds === null) return null;
  const played = seconds - wasSeconds;
  const gained = energy - wasEnergy;
  if (played < MIN_VOICE_SECONDS || gained < 0) return null;
  // Three decimals: quiet speech sits around 0.01–0.03 and anything coarser
  // rounds the whole complaint away.
  return Math.round(Math.sqrt(gained / played) * 1000) / 1000;
}

/** `video/H264` → `H264`, the same shape the diagnostic line prints. */
function codecOf(stats: readonly StatEntry[], id: string | undefined): string | null {
  if (id === undefined) return null;
  const mime = stats.find((entry) => entry.id === id)?.mimeType;
  return mime === undefined ? null : (mime.split('/')[1] ?? mime);
}

/**
 * The fields worth keeping for one call but not worth a column: which encoder
 * and decoder the device actually used, and how much had to be asked for again.
 * Null when nothing was answered, so the column stays empty rather than holding
 * an empty object for every sample of every call.
 */
function extraOf(
  outbound: StatEntry | undefined,
  inbound: StatEntry | undefined,
  source: StatEntry | undefined,
): Record<string, string | number> | null {
  const extra: Record<string, string | number> = {};
  if (outbound?.encoderImplementation) extra.encoder = outbound.encoderImplementation;
  if (inbound?.decoderImplementation) extra.decoder = inbound.decoderImplementation;
  const nack = number(inbound?.nackCount);
  const pli = number(inbound?.pliCount);
  if (nack !== null && nack > 0) extra.nack = nack;
  if (pli !== null && pli > 0) extra.pli = pli;
  // What the camera itself produced, which no other figure in a sample says.
  // «Чёрный экран» arrives as two different faults that outbound-rtp cannot
  // tell apart: a camera that stopped delivering — cam 0 fps while the encoder
  // goes on sending its last frame — and a camera delivering a still black
  // picture at its full rate, which encodes to twenty kilobits and reads as a
  // starved network. One line here decides which of the two happened.
  const cameraFps = number(source?.framesPerSecond);
  if (cameraFps !== null) extra.camFps = Math.round(cameraFps);
  if (source?.width && source.height) extra.cam = `${source.width}×${source.height}`;
  return Object.keys(extra).length === 0 ? null : extra;
}

/**
 * One reading, turned into a sample and the cursor the next one will need.
 *
 * `at` is passed in rather than read from the clock so this stays pure and its
 * tests stay about arithmetic. A report with nothing in it — a connection that
 * refused `getStats`, or one that has not started — yields a sample of nulls
 * rather than nothing at all: the gap is itself worth plotting, and a row that
 * exists with no numbers in it says «this side was here and had nothing to
 * say», which is a different fact from a side that stopped reporting.
 */
export function sampleFrom(
  stats: readonly StatEntry[],
  cursor: StatsCursor | null,
  at: number,
): { sample: CallSample; cursor: StatsCursor } {
  const videoOut = stats.find((entry) => entry.type === 'outbound-rtp' && streamKind(entry) === 'video');
  const videoIn = stats.find((entry) => entry.type === 'inbound-rtp' && streamKind(entry) === 'video');
  const audioOut = stats.find((entry) => entry.type === 'outbound-rtp' && streamKind(entry) === 'audio');
  const audioIn = stats.find((entry) => entry.type === 'inbound-rtp' && streamKind(entry) === 'audio');
  // What the microphone captured, before the encoder had anything to do with
  // it. outbound-rtp carries no level of its own — the sender's loudness is a
  // fact about the capture, and this is where the standard keeps it.
  const audioSource = stats.find((entry) => entry.type === 'media-source' && streamKind(entry) === 'audio');
  // The camera, for the same reason: what was captured, before the encoder.
  const videoSource = stats.find((entry) => entry.type === 'media-source' && streamKind(entry) === 'video');
  const pair = selectedPair(stats);
  const { reason, share } = limitation(videoOut);
  const connection = summariseConnection(stats);

  const counters = {
    at,
    audioOutBytes: number(audioOut?.bytesSent),
    audioInBytes: number(audioIn?.bytesReceived),
    videoOutBytes: number(videoOut?.bytesSent),
    videoInBytes: number(videoIn?.bytesReceived),
    // Both directions of the loss arithmetic come from the inbound streams:
    // what this side *received* and what it noticed missing. Audio and video
    // are summed — one figure per side is what the chart wants, and a call
    // where only the picture is losing packets is visible in the freezes.
    packetsReceived: sum(number(audioIn?.packetsReceived), number(videoIn?.packetsReceived)),
    packetsLost: sum(number(audioIn?.packetsLost), number(videoIn?.packetsLost)),
    audioOutEnergy: number(audioSource?.totalAudioEnergy),
    audioOutSeconds: number(audioSource?.totalSamplesDuration),
    audioInEnergy: number(audioIn?.totalAudioEnergy),
    audioInSeconds: number(audioIn?.totalSamplesDuration),
  };

  const intervalMs = cursor === null ? null : at - cursor.at;
  const jitter = number(audioIn?.jitter) ?? number(videoIn?.jitter);

  const sample: CallSample = {
    at,
    intervalMs,
    audioOutBytes: counters.audioOutBytes,
    audioInBytes: counters.audioInBytes,
    videoOutBytes: counters.videoOutBytes,
    videoInBytes: counters.videoInBytes,
    audioOutBps: rate(counters.audioOutBytes, cursor?.audioOutBytes ?? null, intervalMs),
    audioInBps: rate(counters.audioInBytes, cursor?.audioInBytes ?? null, intervalMs),
    videoOutBps: rate(counters.videoOutBytes, cursor?.videoOutBytes ?? null, intervalMs),
    videoInBps: rate(counters.videoInBytes, cursor?.videoInBytes ?? null, intervalMs),
    audioOutLevel: voiceLevel(
      counters.audioOutEnergy,
      counters.audioOutSeconds,
      cursor?.audioOutEnergy ?? null,
      cursor?.audioOutSeconds ?? null,
    ),
    audioInLevel: voiceLevel(
      counters.audioInEnergy,
      counters.audioInSeconds,
      cursor?.audioInEnergy ?? null,
      cursor?.audioInSeconds ?? null,
    ),
    fpsOut: number(videoOut?.framesPerSecond),
    fpsIn: number(videoIn?.framesPerSecond),
    widthOut: number(videoOut?.frameWidth),
    heightOut: number(videoOut?.frameHeight),
    widthIn: number(videoIn?.frameWidth),
    heightIn: number(videoIn?.frameHeight),
    rttMs: pair?.currentRoundTripTime === undefined ? null : Math.round(pair.currentRoundTripTime * 1000),
    // Seconds in the standard, milliseconds everywhere a person reads them.
    jitterMs: jitter === null ? null : Math.round(jitter * 1000),
    loss: lossOver(counters, cursor),
    availableOutBps:
      pair?.availableOutgoingBitrate === undefined ? null : Math.round(pair.availableOutgoingBitrate),
    limitation: reason,
    limitedShare: share,
    freezes: number(videoIn?.freezeCount),
    freezeMs:
      videoIn?.totalFreezesDuration === undefined
        ? null
        : Math.round(videoIn.totalFreezesDuration * 1000),
    route: connection.route,
    relayed: connection.route === null ? null : connection.route.includes('relay'),
    codecOut: codecOf(stats, videoOut?.codecId) ?? codecOf(stats, audioOut?.codecId),
    codecIn: codecOf(stats, videoIn?.codecId) ?? codecOf(stats, audioIn?.codecId),
    extra: extraOf(videoOut, videoIn, videoSource),
  };

  return { sample, cursor: counters };
}

/** Two figures that may each be absent; null only when both are. */
function sum(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
}

/**
 * Loss over the interval, not over the call.
 *
 * The cumulative share is what `summariseMedia` reports and it is the right
 * answer to «was this call lossy». It is the wrong answer to «what happened at
 * 0:35»: ten bad seconds in a five-minute call average away to nothing, and the
 * chart would show a flat line through the exact moment the picture froze.
 */
function lossOver(current: StatsCursor, previous: StatsCursor | null): number | null {
  if (previous === null) return null;
  if (current.packetsReceived === null || current.packetsLost === null) return null;
  if (previous.packetsReceived === null || previous.packetsLost === null) return null;
  const received = current.packetsReceived - previous.packetsReceived;
  const lost = current.packetsLost - previous.packetsLost;
  const arrived = received + lost;
  // A negative delta is a counter that restarted; an empty interval has no
  // share to report. Both are «could not measure», not «lost nothing».
  if (received < 0 || lost < 0 || arrived <= 0) return null;
  return lost / arrived;
}

/**
 * The first line of a call's log: who this side is and what it was set to.
 *
 * Everything here is either invisible in the samples or expensive to work out
 * from them afterwards — which shell, which implementation of the call, what
 * the tuning was at the moment the connection was built. `native` and `shell`
 * are both here and are not the same fact: the first is which peer carried the
 * media, the second is which app the person was in, and on Android they part
 * company permanently. It goes in as an event rather than a column because it
 * is prose, and because the two sides of one call write one each.
 */
export function helloDetail(facts: {
  native: boolean;
  shell: string;
  video: boolean;
  direction: 'outgoing' | 'incoming';
  hasRelay: boolean;
  /** Whether the caller opened relay-only — what explains a relayed route between two phones on one Wi-Fi. */
  relayFirst: boolean;
  width: number;
  height: number;
  fps: number;
  videoKbps: number;
  audioKbps: number;
  degradation: string;
  codec: string;
}): string {
  return [
    facts.direction,
    facts.video ? 'video' : 'audio',
    facts.native ? 'native' : 'page',
    // Which app, beside which peer. The two look like one fact and are not:
    // the Android shell runs the page's own peer, so `page` there says nothing
    // about whether anybody was in a browser. This is the half that does.
    `shell=${facts.shell}`,
    facts.relayFirst ? 'relay first' : facts.hasRelay ? 'relay configured' : 'no relay',
    `${facts.width}×${facts.height}@${facts.fps}`,
    `${facts.videoKbps}/${facts.audioKbps}kbps`,
    facts.degradation,
    `codec ${facts.codec}`,
  ].join(' · ');
}
